import { EffectsModule } from "@ngrx/effects";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgxPiwikProModule, NgxPiwikProRouterModule } from "@piwikpro/ngx-piwik-pro";
import * as Sentry from "@sentry/angular";
import { TruncateModule } from "@yellowspot/ng-truncate";
import { AngularSvgIconModule } from "angular-svg-icon";
import { HighchartsChartModule } from "highcharts-angular";
import { NgMultiSelectDropDownModule } from "ng-multiselect-dropdown";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";

import { registerLocaleData } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from "@angular/common/http";
import localeNl from "@angular/common/locales/nl";
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";

import { provideApiConfiguration as provideNwdApiConfiguration } from "../apis/networkdashboard/rest/api-configuration";
import { provideApiConfiguration as provideStatsApiConfiguration } from "../apis/stats/rest/api-configuration";
import { provideApiConfiguration as provideDomainsApiConfiguration } from "../apis/surfdomeinen/api-configuration";
import { ApiHelper } from "../helpers/apihelper";
import { NWDHttpInterceptor } from "../helpers/interceptor/http.interceptor";
import { WebsocketService } from "../helpers/websocket.service";
import { IotnetRepositoryProvider } from "../pages/iotnet/iotnet-repository.provider";
import { AuthService } from "../services/authservice";
import { MessageService } from "../services/messageservice";
import { NWD_VERSION } from "../version";
import { AppRoutingModule } from "./app-routing.module";
import { ENV } from "./app.runtime";
import { AppInitializerTokenProvider } from "./auth/app-initializer-token";
import { AuthHttpConfigModule } from "./auth/auth-http-config.module";
import { RssNewsRepository } from "./news/adapters/rss-news-repository.adapter";
import { NEWS_REPOSITORY } from "./news/domain/ports/news-repository.port";
import { NewsModule } from "./news/news.module";
import { NwdSharedModule } from "./nwd-shared/nwd-shared.module";

registerLocaleData(localeNl);

const piwikModules =
  ENV.PIWIK_SITEID ?
    [
      NgxPiwikProModule.forRoot(ENV.PIWIK_SITEID, "https://surfnl.containers.piwik.pro"),
      NgxPiwikProRouterModule.forRoot({ exclude: ["/oauth2/callback"], skipFirstPageView: false }),
    ]
  : [];

const providers = [
  provideZoneChangeDetection(),
  provideHttpClient(withInterceptorsFromDi(), withFetch()),
  importProvidersFrom(
    NwdSharedModule,
    AngularSvgIconModule.forRoot(),
    AppRoutingModule,
    TruncateModule,
    HighchartsChartModule,
    NgxSkeletonLoaderModule,
    NgMultiSelectDropDownModule.forRoot(),
    StoreModule.forRoot({}),
    StoreDevtoolsModule.instrument({ maxAge: 25 }),
    EffectsModule.forRoot([]),
    NewsModule,
    AuthHttpConfigModule,
    ...piwikModules,
  ),
  provideTranslateService({
    loader: provideTranslateHttpLoader({ prefix: "./assets/i18n/", suffix: `.json?v=${NWD_VERSION}` }),
  }),
  provideNwdApiConfiguration(`${ENV.DOMAINS_API}`), // Yes, using the DOMAINS API host is correct, because it is the same. The NWD API env vars are wrong due to path issues.
  provideDomainsApiConfiguration(`${ENV.DOMAINS_API}`),
  provideStatsApiConfiguration(`${ENV.DOMAINS_API}`), // Yes, using the DOMAINS API host is correct, because it is the same. The NWD API env vars are wrong due to path issues.
  ApiHelper,
  AuthService,
  MessageService,
  WebsocketService,
  { provide: HTTP_INTERCEPTORS, useClass: NWDHttpInterceptor, multi: true },
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: false,
    }),
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  provideAppInitializer(() => {
    inject(Sentry.TraceService);
  }),
  AppInitializerTokenProvider,
  { provide: NEWS_REPOSITORY, useClass: RssNewsRepository },
  IotnetRepositoryProvider,
];

if (ENV.ENABLED_ROUTES.split(",").includes("dashboard")) {
  // TODO Make dashboard, news, domains part of an enum
  // TODO Put dashboard specific declarations, providers, etc. in dashboard module
  // Hack: In case we have dashboard routes enabled, we want to set the site title to Netwerk Dashboard
  // This is done like this, because when we only enable the news routes, we don't want to see Netwerk Dashboard in the title
  providers.push(
    provideAppInitializer(() => {
      const initializerFn = (
        (title: Title) => () =>
          title.setTitle("Netwerk Dashboard")
      )(inject(Title));
      return initializerFn();
    }),
  );
}

export const appConfig: ApplicationConfig = { providers };
